import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-loomlite-purple/70 disabled:opacity-50 disabled:pointer-events-none',
	{
		variants: {
			variant: {
				default: 'bg-loomlite-purple text-white hover:bg-[#9B1FD9]',
				outline:
					'border border-loomlite-border-strong bg-transparent text-loomlite-text hover:bg-white/5',
				ghost: 'text-loomlite-text hover:bg-white/5',
				destructive: 'bg-red-600/90 text-white hover:bg-red-600',
				gold: 'bg-loomlite-gold text-loomlite-ink hover:bg-loomlite-gold-soft',
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-8 px-3 text-xs',
				lg: 'h-12 px-6 text-base',
				icon: 'h-10 w-10',
			},
		},
		defaultVariants: { variant: 'default', size: 'default' },
	},
);

export interface ButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
		);
	},
);
Button.displayName = 'Button';
